// src/pages/BolPrintPage.js
import React, { useState, useEffect, useMemo } from 'react';
import bolService from '../services/bolService';
import { usePermissions } from '../context/PermissionContext';
import { useLanguage, t } from '../context/LanguageContext';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtWeight(val, unit) {
  if (!val && val !== 0) return '—';
  return `${Number(val).toLocaleString()} ${unit}`;
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
}

function groupItemsByProduct(items) {
  const groups = [];
  const seen = new Map();
  for (const item of items) {
    const key = `${item.productId || ''}|${item.productName}`;
    if (!seen.has(key)) {
      seen.set(key, { productName: item.productName, productId: item.productId, rows: [] });
      groups.push(seen.get(key));
    }
    seen.get(key).rows.push(item);
  }
  return groups;
}

// ── Print View ────────────────────────────────────────────────────────────────

function BolPrintView({ bol, companyName, companyAddr, logoUrl, title, subtitle, showRefrig, showDisclaimer, disclaimer, refrigChecks }) {
  const { lang } = useLanguage();
  const groups = groupItemsByProduct(bol.Items || []);

  const totals = useMemo(() => {
    const items = bol.Items || [];
    let cases = 0, netByUnit = {}, grossByUnit = {};
    for (const it of items) {
      cases += Number(it.quantityCases) || 0;
      const net = Number(it.netWeightValue) || 0;
      const gross = Number(it.grossWeightValue) || 0;
      netByUnit[it.netWeightUnit] = (netByUnit[it.netWeightUnit] || 0) + net;
      grossByUnit[it.grossWeightUnit] = (grossByUnit[it.grossWeightUnit] || 0) + gross;
    }
    const netStr = Object.entries(netByUnit).map(([u, v]) => `${v.toLocaleString()} ${u}`).join(' + ') || '—';
    const grossStr = Object.entries(grossByUnit).map(([u, v]) => `${v.toLocaleString()} ${u}`).join(' + ') || '—';
    return { cases, netStr, grossStr };
  }, [bol.Items]);

  const totalPallets = new Set((bol.Items || []).map(it => it.palletId).filter(Boolean)).size || (bol.Items || []).length;
  const poLines = (bol.customerPO || '').split('\n').filter(Boolean);
  const shipToName = bol.sameAsConsignee ? bol.consigneeName : (bol.shipToName || bol.consigneeName);
  const shipToAddr = bol.sameAsConsignee ? bol.consigneeAddress : (bol.shipToAddress || bol.consigneeAddress);
  const enabledChecks = (refrigChecks || []).filter(c => c.enabled);

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#000' }}>

      {/* Header — logo or company name */}
      <div style={{ textAlign: 'center', marginBottom: 10, position: 'relative' }}>
        <div style={{ position: 'absolute', top: 0, right: 0, fontSize: 11 }}>1 of 1</div>
        {logoUrl
          ? <img
              src={logoUrl}
              alt={companyName}
              style={{ maxHeight: 90, maxWidth: 360, objectFit: 'contain', display: 'block', margin: '0 auto 6px' }}
            />
          : <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: 10 }}>{companyName.toUpperCase()}</div>
        }
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 12 }}>{title}</div>
        {subtitle && <div style={{ fontSize: 11 }}>{subtitle}</div>}
      </div>

      {/* Info block — 3 columns */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12, fontSize: 11 }}>
        <tbody>
          <tr style={{ verticalAlign: 'top' }}>
            <td style={{ width: '33%', paddingRight: 12 }}>
              <div style={{ fontWeight: 700 }}>{t('Shipper:', 'Remitente:', lang)}</div>
              <div>{companyName}</div>
              <div style={{ whiteSpace: 'pre-line' }}>{companyAddr}</div>
            </td>
            <td style={{ width: '34%', paddingRight: 12 }}>
              <div style={{ fontWeight: 700 }}>{t('Consignee:', 'Consignatario:', lang)}</div>
              <div>{bol.consigneeName}</div>
              <div style={{ whiteSpace: 'pre-line' }}>{bol.consigneeAddress}</div>
            </td>
            <td style={{ width: '33%' }}>
              {poLines.length > 0 && (
                <>
                  <div style={{ fontWeight: 700 }}>{t('Customer POs:', 'OC del Cliente:', lang)}</div>
                  {poLines.map((po, i) => <div key={i} style={{ marginLeft: 8 }}>{po}</div>)}
                </>
              )}
              <div style={{ marginTop: 6 }}><span style={{ fontWeight: 700 }}>{t('Invoice #:', 'Factura #:', lang)} </span>{bol.invoiceNumber || ''}</div>
              <div><span style={{ fontWeight: 700 }}>{t('Ship Date:', 'Fecha Envío:', lang)} </span>{fmtDate(bol.shipDate)}</div>
              <div><span style={{ fontWeight: 700 }}>{t('Ship Via:', 'Vía de Envío:', lang)} </span>{bol.shipVia || ''}</div>
            </td>
          </tr>
          {(shipToName || shipToAddr) && (
            <tr style={{ verticalAlign: 'top' }}>
              <td style={{ paddingTop: 8, paddingRight: 12 }}>
                <div style={{ fontWeight: 700 }}>{t('Ship To:', 'Enviar A:', lang)}</div>
                <div>{shipToName}</div>
                <div style={{ whiteSpace: 'pre-line' }}>{shipToAddr}</div>
              </td>
              <td /><td />
            </tr>
          )}
        </tbody>
      </table>

      {/* Items table — grouped by product */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #000' }}>
            <th style={{ textAlign: 'left', paddingBottom: 4, width: '22%' }}>{t('Lot / Exp. Date', 'Lote / F. Venc.', lang)}</th>
            <th style={{ textAlign: 'center', width: '14%' }}>{t('Pallet ID', 'ID Pallet', lang)}</th>
            <th style={{ textAlign: 'center', width: '16%' }}>{t('Quantity (CS)', 'Cantidad (CJ)', lang)}</th>
            <th style={{ textAlign: 'center', width: '18%' }}>{t('Net Wgt', 'Peso Neto', lang)}</th>
            <th style={{ textAlign: 'center', width: '18%' }}>{t('Gross Wgt', 'Peso Bruto', lang)}</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group, gi) => (
            <React.Fragment key={gi}>
              {gi > 0 && (
                /* Spacer between product groups */
                <tr><td colSpan={5} style={{ height: 6 }} /></tr>
              )}
              <tr>
                <td colSpan={5} style={{ fontWeight: 700, paddingTop: 4, paddingBottom: 3, fontSize: 12, borderBottom: '1px solid #ddd' }}>
                  {group.productName}
                </td>
              </tr>
              {group.rows.map((item, ri) => (
                <tr key={ri} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ paddingLeft: 8, paddingTop: 4, paddingBottom: 4 }}>
                    <span style={{ fontFamily: 'monospace' }}>{item.lotCode}</span>
                    {item.expirationDate && (
                      <span style={{ fontSize: 10, color: '#555', marginLeft: 6 }}>· {t('Exp:', 'Venc:', lang)} {fmtDate(item.expirationDate)}</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>{item.palletId}</td>
                  <td style={{ textAlign: 'center' }}>{item.quantityCases}</td>
                  <td style={{ textAlign: 'center' }}>{fmtWeight(item.netWeightValue, item.netWeightUnit)}</td>
                  <td style={{ textAlign: 'center' }}>{fmtWeight(item.grossWeightValue, item.grossWeightUnit)}</td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #000', fontWeight: 700 }}>
            <td style={{ paddingTop: 8 }}>{t('Totals', 'Totales', lang)}</td>
            <td />
            <td style={{ textAlign: 'center', paddingTop: 8 }}>{totals.cases}</td>
            <td style={{ textAlign: 'center', paddingTop: 8 }}>{totals.netStr}</td>
            <td style={{ textAlign: 'center', paddingTop: 8 }}>{totals.grossStr}</td>
          </tr>
        </tfoot>
      </table>

      {/* Disclaimer */}
      {showDisclaimer && disclaimer && (
        <p style={{ fontSize: 10, marginTop: 24, marginBottom: 20, textAlign: 'justify' }}>{disclaimer}</p>
      )}

      {/* Refrigeration checks */}
      {showRefrig && enabledChecks.length > 0 && (
        <div style={{ marginTop: showDisclaimer && disclaimer ? 0 : 24, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 32px' }}>
            {enabledChecks.map(check => (
              <div key={check.id} style={{ display: 'flex', alignItems: 'baseline', gap: 8, borderBottom: '1px solid #ccc', paddingBottom: 4 }}>
                <span style={{ minWidth: 160 }}>{check.label}:</span>
                {check.type === 'text' && <span>{check.defaultValue || ''}</span>}
                {check.type === 'yes_no' && <span>{t('Yes', 'Sí', lang)} _____ / {t('No', 'No', lang)} _____</span>}
                {check.type === 'temperature' && <span style={{ flex: 1 }}>_____________ °{check.unit || 'F'}</span>}
                {check.type === 'number' && <span style={{ flex: 1 }}>{check.autoFill === 'palletCount' ? totalPallets : '____________'}</span>}
                {(check.type === 'text_input' || check.type === 'signature_line') && (
                  <span style={{ flex: 1, borderBottom: '1px solid #000', minWidth: 140 }}>&nbsp;</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Carrier signature */}
      <div style={{ marginTop: 24, display: 'flex', gap: 60 }}>
        <div>{t('Carrier Signature:', 'Firma del Transportista:', lang)} <span style={{ display: 'inline-block', borderBottom: '1px solid #000', minWidth: 200 }}>&nbsp;</span></div>
        <div>{t('Date:', 'Fecha:', lang)} <span style={{ display: 'inline-block', borderBottom: '1px solid #000', minWidth: 200 }}>&nbsp;</span></div>
      </div>
    </div>
  );
}

// ── Page Component ────────────────────────────────────────────────────────────

export default function BolPrintPage() {
  const { lang } = useLanguage();
  const { companySettings } = usePermissions();
  const [bol, setBol] = useState(null);
  const [bolSettings, setBolSettings] = useState(null);
  const [error, setError] = useState('');

  const params = new URLSearchParams(window.location.search);
  const bolId = params.get('id');
  const isPreview = params.get('preview') === '1';

  useEffect(() => {
    bolService.getSettings().then(setBolSettings).catch(() => {});

    if (bolId) {
      bolService.getBol(bolId)
        .then(setBol)
        .catch(() => setError(t('Could not load BOL.', 'No se pudo cargar el BOL.', lang)));
    } else if (isPreview) {
      try {
        const raw = localStorage.getItem('bolPreview');
        if (raw) {
          setBol(JSON.parse(raw));
          // Do NOT removeItem here — React Strict Mode runs this effect twice in dev,
          // so removing on first run leaves nothing for the second run.
          // The item gets overwritten on the next Preview click anyway.
        } else {
          setError(t('No preview data found. Please close this tab and click Preview / Print again.', 'No se encontraron datos de vista previa. Cierra esta pestaña y haz clic en Vista Previa / Imprimir nuevamente.', lang));
        }
      } catch {
        setError(t('Failed to read preview data.', 'Error al leer los datos de vista previa.', lang));
      }
    } else {
      setError(t('No BOL specified.', 'No se especificó un BOL.', lang));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const companyName = companySettings?.name || 'Company';
  const logoUrl = companySettings?.logoUrl || null;
  const companyAddr = [
    companySettings?.addressLine1,
    companySettings?.city,
    companySettings?.stateProvince,
    companySettings?.postalCode,
    companySettings?.country,
  ].filter(Boolean).join(', ');

  const title = bolSettings?.bolTitle || 'STRAIGHT BILL OF LADING (NON-NEGOTIABLE)';
  const subtitle = bolSettings?.bolSubtitle ?? null;
  const showRefrig = bolSettings?.bolShowRefrigerationSection !== false;
  const showDisclaimer = bolSettings?.bolShowDisclaimer !== false;
  const disclaimer = bolSettings?.bolDisclaimerText || '';
  const refrigChecks = bolSettings?.bolRefrigerationChecks || [];

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">{error}</div>
        <button className="btn btn-secondary" onClick={() => window.close()}>{t('Close', 'Cerrar', lang)}</button>
      </div>
    );
  }

  if (!bol) {
    return (
      <div className="container mt-5 text-center">
        <span className="spinner-border" />
      </div>
    );
  }

  return (
    <div>
      {/* Screen-only toolbar — hidden when printing */}
      <div className="d-print-none" style={{ background: '#1b2638', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12, position: 'sticky', top: 0, zIndex: 2000 }}>
        <span style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>
          {isPreview ? t('Preview (Draft)', 'Vista Previa (Borrador)', lang) : `BOL ${bol.bolNumber}`}
        </span>
        <div className="ms-auto d-flex gap-2">
          <button className="btn btn-primary btn-sm" onClick={() => window.print()}>{t('Print', 'Imprimir', lang)}</button>
          <button className="btn btn-outline-light btn-sm" onClick={() => window.close()}>{t('Close', 'Cerrar', lang)}</button>
        </div>
      </div>

      {/* Print content */}
      <div style={{ maxWidth: 860, margin: '24px auto', background: '#fff', padding: '32px 40px', boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }}
           className="d-print-block">
        <BolPrintView
          bol={bol}
          companyName={companyName}
          companyAddr={companyAddr}
          logoUrl={logoUrl}
          title={title}
          subtitle={subtitle}
          showRefrig={showRefrig}
          showDisclaimer={showDisclaimer}
          disclaimer={disclaimer}
          refrigChecks={refrigChecks}
        />
      </div>

      {/* Ensure clean print — remove screen padding/shadow */}
      <style>{`
        @media print {
          body { margin: 0; }
          div[style*="maxWidth: 860px"], div[style*="max-width: 860px"] {
            box-shadow: none !important;
            margin: 0 !important;
            padding: 10mm 12mm !important;
          }
        }
      `}</style>
    </div>
  );
}
